import React, {useState, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {Box, Dialog, Typography, FormControl, Slide} from '@mui/material';
import Rating from '@mui/material/Rating';
import withStyles from '@mui/styles/withStyles';
import RadioGroup from '@mui/material/RadioGroup';
import RefreshIcon from '@mui/icons-material/Refresh';
import {fetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import {API_URL} from '../../constants';
import * as Sentry from '@sentry/react';
import {useSetRecoilState} from 'recoil';
import {loadingState} from '../../recoil/bidmiiCustomLoader/bidmiiCustomLoaderAtom';
import useScreenWidth from '../../hooks/useScreenWidth';
import PrimaryButton from '../../common/StyledComponents/PrimaryButton';
import {theme} from '../../Theme';
import BidmiiRadio from '../../common/StyledComponents/BidmiiRadio';
import BidmiiTextField from '../../common/StyledComponents/BidmiiTextField';
import BidmiiCheckbox from '../../common/StyledComponents/BidmiiCheckbox';
import FixedFooter from '../../common/FixedFooter';
import ToolbarHeader from '../../common/ToolbarHeader';
import BidmiiImageCarousel from '../../common/BidmiiImageCarousel';
import useCelebrationLottie from '../../hooks/useCelebrationLottie';
import getTotalStages from '../../util/getTotalStages';
import Card from '../../common/design/Card';
import StatusChip from '../../common/design/StatusChip';
import {SkeletonBlock} from '../../common/design/Skeletons';
import {fonts, kit, radius} from '../../common/design/tokens';

const Transition = React.forwardRef(function Transition(props, ref) {
  // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children?: ReactNode; direction: "up"; ref... Remove this comment to see the full error message
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledRating = withStyles({
  label: {
    fontSize: 32,
  },
  iconFilled: {
    color: theme.palette.blue.main,
  },
  iconHover: {
    color: theme.palette.blue.main,
  },
})(Rating);

type WorkflowStep = 'approval' | 'review' | 'completion';

export default function ReviewWorkflow() {
  const params = useParams<{rfpId: string}>();
  const width = useScreenWidth();
  const setLoading = useSetRecoilState(loadingState);

  const [step, setStep] = useState<WorkflowStep>('approval');
  const [requestForProposal, setRequestForProposal] = useState<any>(null);
  const [proposal, setProposal] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [workComplete, setWorkComplete] = useState(false);
  const [score, setScore] = useState<string | null>(null);
  const [showRequiredError, setShowRequiredError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [review, setReview] = useState<any>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [ratings, setRatings] = useState<Record<string, number | null>>({});
  const [missingRatingIds, setMissingRatingIds] = useState<string[]>([]);

  const [celebrationAnimation, startCelebration] = useCelebrationLottie({});

  // Fetch RFP data and proposal data on mount (and whenever handleRetry bumps
  // retryCount, e.g. after a network error) -- same request, same conditions.
  useEffect(() => {
    const fetchData = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const code = searchParams.get('code');

      if (!code) {
        setError('Authorization code is required');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        // Fetch RFP data
        const rfpData = await fetchRequest(
          `${API_URL}/rfps/private/${params.rfpId}?code=${code}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        ).then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              throw new Error('Invalid or expired authorization code');
            }
            if (res.status === 404) {
              throw new Error('RFP not found');
            }
            throw new Error(`Failed to fetch RFP: ${res.status}`);
          }
          return res.json();
        });

        setRequestForProposal(rfpData);

        // Fetch proposal data after RFP is loaded
        if (rfpData?._links?.proposal?.href) {
          const proposalHref = rfpData._links.proposal.href;
          const proposalUid = proposalHref.split('/').pop()?.split('?')[0];

          if (proposalUid) {
            const proposalData = await fetchRequest(
              `${API_URL}/proposals/${proposalUid}?code=${code}`,
              {
                headers: {
                  'Content-Type': 'application/json',
                },
              },
            ).then((res) => {
              if (!res.ok) {
                throw new Error(`Failed to fetch proposal: ${res.status}`);
              }
              return res.json();
            });

            setProposal(proposalData);
          }
        }
      } catch (err: any) {
        process.env.NODE_ENV === 'development' && console.error(err);
        setError(err.message || 'Failed to load data');
        Sentry.withScope(function (scope) {
          scope.setTag('section', 'ReviewWorkflow.tsx fetchData()');
          Sentry.captureException(err);
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.rfpId, retryCount]);

  const handleRetry = () => {
    setError(null);
    setRetryCount((count) => count + 1);
  };

  // Determine which review to use - defaulting to requesterReview for now
  // In production, this would be determined by the user context or code parameter
  useEffect(() => {
    if (requestForProposal) {
      // Defaulting to bidderReview (homeowner reviewing contractor)
      const reviewToUse = requestForProposal?.bidderReview;
      setReview(reviewToUse);
    }
  }, [requestForProposal]);

  // Determine initial step based on stage and review states
  useEffect(() => {
    if (!proposal || !review) {
      return;
    }

    // If review is already complete, always show completion screen
    // This check must come first to prevent other logic from overriding completion step
    if (review.state === 'COMPLETE') {
      if (step !== 'completion') {
        setStep('completion');
        // Start celebration animation for already-completed reviews
        setTimeout(() => {
          startCelebration();
        }, 100);
      }
      return;
    }

    const allStages = getTotalStages(proposal);
    const incompleteStages = allStages.filter(
      (stage: $TSFixMe) => stage.state !== 'COMPLETE',
    );
    const pendingApprovalStages = allStages.filter(
      (stage: $TSFixMe) => stage.state === 'APPROVAL_PENDING',
    );

    // If all stages are COMPLETE, move to review step
    if (incompleteStages.length === 0) {
      setStep('review');
      return;
    }

    // If there are no APPROVAL_PENDING stages but stages are incomplete,
    // they may have been approved already, so check for next stage
    if (pendingApprovalStages.length === 0 && incompleteStages.length > 0) {
      // All pending stages have been approved, move to review
      setStep('review');
      return;
    }

    // Default to approval step if there are stages pending approval
    if (pendingApprovalStages.length > 0) {
      setStep('approval');
    }
  }, [proposal, review, startCelebration, step]);

  const revieweeType =
    review?.reviewType === 'REQUESTER' ? 'contractor' : 'homeowner';

  /*
  const isFinalStage =
    proposal &&
    (getTotalStages(proposal).filter(
      (stage: $TSFixMe) => stage.state !== 'COMPLETE',
    ).length === 1 ||
      !proposal?.stages ||
      proposal.stages.length === 0);
    */

  // Find the next stage that needs approval (skip already approved/complete stages)
  const getNextPendingStage = () => {
    if (!proposal) {
      return null;
    }
    const allStages = getTotalStages(proposal);
    return allStages.find(
      (stage: $TSFixMe) => stage.state === 'APPROVAL_PENDING',
    );
  };

  // Check if all stages are complete
  const areAllStagesComplete = () => {
    if (!proposal) {
      return false;
    }
    const allStages = getTotalStages(proposal);
    return (
      allStages.length > 0 &&
      allStages.every((stage: $TSFixMe) => stage.state === 'COMPLETE')
    );
  };

  // Ratings are tracked in React state (controlled) rather than read back
  // from the form at submit time: on iOS Safari a tapped star can render
  // filled without the underlying radio ever committing, so FormData came
  // back empty while the user saw a completed rating (prod user 10242,
  // 2026-10-17 -- blocked on both devices with "ratings are required").
  const getMissingRatingIds = () =>
    (review?.reviewQuestions || [])
      .filter((question: $TSFixMe) => !ratings[`${question.id}`])
      .map((question: $TSFixMe) => `${question.id}`);

  const handleApprovalSubmit = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');

    if (!code) {
      setError('Authorization code is required');
      return;
    }

    setIsSubmitting(true);

    // Check if all stages are complete - if so, move directly to review
    const allComplete = areAllStagesComplete();
    if (allComplete) {
      setIsSubmitting(false);
      setStep('review');
      return;
    }

    // Find the next stage that needs approval
    const stageToApprove = getNextPendingStage();

    if (stageToApprove) {
      setLoading(true);
      try {
        await fetchRequest(
          `${API_URL}/proposals/stages/${stageToApprove.uid}/approve?code=${code}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          },
        ).then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              throw new Error('Invalid or expired authorization code');
            }
            if (res.status === 404) {
              throw new Error('Stage not found');
            }
            if (res.status === 400) {
              throw new Error('Stage cannot be approved');
            }
            throw new Error(`Failed to approve stage: ${res.status}`);
          }
          return res.json();
        });

        // After approving, check if there are more stages or if we should move to review
        // Refetch proposal data to get updated stage states
        const updatedProposal = await fetchRequest(
          `${API_URL}/proposals/${proposal.uid}?code=${code}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        ).then((res) => {
          if (!res.ok) {
            throw new Error(`Failed to fetch updated proposal: ${res.status}`);
          }
          return res.json();
        });

        setProposal(updatedProposal);

        // Check if all stages are now complete
        const allStages = getTotalStages(updatedProposal);
        const remainingPendingStages = allStages.filter(
          (stage: $TSFixMe) => stage.state === 'APPROVAL_PENDING',
        );

        setLoading(false);
        setIsSubmitting(false);

        // If no more pending stages, move to review
        if (remainingPendingStages.length === 0) {
          setStep('review');
        } else {
          // Stay on approval step for next stage
          setStep('approval');
        }
      } catch (err: any) {
        setLoading(false);
        setIsSubmitting(false);
        setError(err.message || 'Failed to approve stage');
        process.env.NODE_ENV === 'development' && console.error(err);
        Sentry.withScope(function (scope) {
          scope.setTag('section', 'ReviewWorkflow.tsx handleApprovalSubmit()');
          Sentry.captureException(err);
        });
      }
    } else {
      // No stage to approve, move to review
      setIsSubmitting(false);
      setStep('review');
    }
  };

  const handleReviewSubmit = async (e: $TSFixMe) => {
    e.preventDefault();
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');

    if (!code) {
      setError('Authorization code is required');
      return;
    }

    const form = e.target;
    const formdata = new FormData(form);

    const body: $TSFixMe = {};
    Array.from(formdata.entries()).forEach((pair) => {
      const key = pair[0];
      const value = pair[1];
      body[key] = value;
    });

    // State is the source of truth for ratings; FormData radio values are
    // unreliable on iOS (see ratings state comment above).
    (review?.reviewQuestions || []).forEach((question: $TSFixMe) => {
      const value = ratings[`${question.id}`];
      if (value) {
        body[`${question.id}`] = `${value}`;
      }
    });

    const missing = getMissingRatingIds();
    if (missing.length > 0) {
      setMissingRatingIds(missing);
      setShowRequiredError(true);
      const el = document.getElementById(`review_question_${missing[0]}`);
      el?.scrollIntoView({behavior: 'smooth', block: 'center'});
      return;
    }
    setMissingRatingIds([]);
    setShowRequiredError(false);

    setIsSubmitting(true);
    setLoading(true);

    try {
      // Submit review using unsecured fetchRequest with code parameter
      const updatedReview = await fetchRequest(
        `${API_URL}/reviews/${review.uid}?code=${code}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        },
      ).then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            throw new Error('Invalid or expired authorization code');
          }
          if (res.status === 404) {
            throw new Error('Review not found');
          }
          if (res.status === 400) {
            throw new Error('Invalid review data');
          }
          if (res.status === 409) {
            throw new Error('Review already submitted');
          }
          throw new Error(`Failed to submit review: ${res.status}`);
        }
        return res.json();
      });

      // Update the review state with the response to reflect COMPLETE status
      // This must be done BEFORE setting the step to prevent the step determination
      // effect from reverting the step based on stale review state
      setReview(updatedReview);

      setLoading(false);
      setIsSubmitting(false);

      // Set step to completion after updating review state
      // The step determination effect will see review.state === 'COMPLETE' and maintain this step
      setStep('completion');
      startCelebration();
    } catch (err: any) {
      process.env.NODE_ENV === 'development' && console.error(err);
      setLoading(false);
      setIsSubmitting(false);
      setError(err.message || 'Failed to submit review');
      Sentry.withScope(function (scope) {
        scope.setTag('section', 'ReviewWorkflow.tsx handleReviewSubmit()');
        Sentry.captureException(err);
      });
    }
  };

  const getQuestion = (question: $TSFixMe) => {
    return question.split(/\(([^)]+)\)/);
  };

  const renderApprovalStep = () => {
    const currentStage = getNextPendingStage();
    let stageIndex: number | null = null;

    if (currentStage && proposal) {
      const allStages = getTotalStages(proposal);
      const index = allStages.findIndex(
        (stage: $TSFixMe) => stage.uid === currentStage.uid,
      );
      stageIndex = index >= 0 ? index + 1 : null; // 1-indexed
    }

    return (
      <>
        <ToolbarHeader title="" />
        <Box
          sx={{
            backgroundColor: kit.mist,
            padding: '20px 22px 32px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}>
          <Card radius="lg" sx={{padding: '12px'}}>
            <BidmiiImageCarousel
              requestForProposal={requestForProposal}
              stageIndex={stageIndex}
            />
          </Card>

          <Card sx={{padding: '20px 22px'}}>
            <Typography
              sx={{
                fontFamily: fonts.serif,
                fontSize: 22,
                lineHeight: 1.25,
                color: kit.ink,
              }}>
              {stageIndex && currentStage
                ? currentStage.name || `Stage ${stageIndex}`
                : 'Stage is ready for your review'}
            </Typography>
            <Typography
              sx={{
                fontSize: 14.5,
                lineHeight: 1.5,
                color: kit.slate,
                fontWeight: 500,
                marginTop: '8px',
              }}>
              {proposal?.user?.name || 'The contractor'} has completed work on{' '}
              {stageIndex && currentStage
                ? `Stage ${stageIndex}, "${currentStage.name || `Stage ${stageIndex}`}" for `
                : ''}
              your project, "
              {requestForProposal?.title || proposal?.requestForProposalTitle}".
              Please review the project site and photos, and confirm that the
              work is complete, so the payment can be released to{' '}
              {proposal?.user?.name || 'the contractor'}.
            </Typography>
            <Box sx={{marginTop: '16px', marginBottom: '20px'}}>
              <BidmiiCheckbox
                checked={workComplete}
                onChange={(e: any) => setWorkComplete(e.target.checked)}
                label={`${
                  stageIndex ? `Stage ${stageIndex}` : 'Project'
                } work is complete`}
              />
            </Box>

            <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
              <PrimaryButton
                disabled={!workComplete || isSubmitting}
                onClick={handleApprovalSubmit}>
                NEXT
              </PrimaryButton>
            </Box>
          </Card>
        </Box>
      </>
    );
  };

  const renderReviewStep = () => {
    if (!review) {
      return null;
    }

    return (
      <>
        <ToolbarHeader title="" />

        <form
          autoComplete="off"
          onSubmit={handleReviewSubmit}
          encType="multipart/form-data"
          style={{display: 'contents'}}>
          <Box
            id="scrollDiv"
            sx={(theme) => ({
              padding: '24px',
              paddingBottom: '120px',
              height: '100vh',
              maxHeight: '100vh',
              overflowY: 'scroll',
              backgroundColor: kit.mist,
              '& .MuiFormControlLabel-labelPlacementTop': {margin: '-4px'},
              '& .MuiTypography-h5': {fontSize: 26},
              '& .MuiTypography-h6': {fontSize: 22},
              [theme.breakpoints.up('sm')]: {
                padding: '32px',
                paddingBottom: '32px',
                height: '70vh',
                '& .MuiFormControlLabel-labelPlacementTop': {margin: '6px'},
              },
            })}>
            <Box>
              <Card sx={{padding: 0, overflow: 'hidden'}}>
                <Box
                  sx={{
                    padding: '20px 22px',
                    borderBottom: `1px solid ${kit.mist}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '16px',
                  }}>
                  <Typography
                    sx={{
                      fontFamily: fonts.serif,
                      fontSize: 24,
                      lineHeight: 1.25,
                      color: kit.ink,
                    }}>
                    Review {review.reviewed.name}
                  </Typography>
                  {requestForProposal && (
                    <StatusChip status={requestForProposal} />
                  )}
                </Box>

                <Box sx={{padding: '20px 22px'}}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: kit.slate,
                    }}>
                    Public feedback
                  </Typography>
                  <Typography
                    sx={{fontSize: 12.5, color: kit.slate2, marginTop: '2px'}}>
                    This feedback will be shared on the {revieweeType}'s profile
                  </Typography>

                  {showRequiredError && (
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        color: kit.danger,
                        fontWeight: 600,
                        marginTop: '10px',
                      }}>
                      Please add a star rating to each highlighted question
                      below
                    </Typography>
                  )}

                  {review.reviewQuestions.map((question: $TSFixMe) => (
                    <Box
                      key={`review_question_${question.id}`}
                      id={`review_question_${question.id}`}
                      sx={{marginTop: '18px'}}>
                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: 700,
                          lineHeight: 1.3,
                          color: kit.ink,
                        }}>
                        {getQuestion(question.question)[0]}
                      </Typography>
                      {getQuestion(question.question)[1] && (
                        <Typography
                          sx={{
                            fontSize: 12.5,
                            color: kit.slate2,
                            marginTop: '2px',
                          }}>
                          {getQuestion(question.question)[1]}
                        </Typography>
                      )}
                      <StyledRating
                        name={`${question.id}`}
                        precision={0.5}
                        value={ratings[`${question.id}`] ?? null}
                        onChange={(e: $TSFixMe, newValue: number | null) => {
                          setRatings((prev) => ({
                            ...prev,
                            [`${question.id}`]: newValue,
                          }));
                          setMissingRatingIds((prev) =>
                            prev.filter((id) => id !== `${question.id}`),
                          );
                        }}
                        style={{fontSize: 40, marginTop: 8}}
                      />
                      {showRequiredError &&
                        missingRatingIds.includes(`${question.id}`) && (
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              color: kit.danger,
                              fontWeight: 600,
                            }}>
                            Tap the stars to rate this
                          </Typography>
                        )}
                    </Box>
                  ))}

                  <Box sx={{marginTop: '20px'}}>
                    <Typography
                      sx={{fontSize: 14.5, fontWeight: 700, color: kit.ink}}>
                      Additional comments{' '}
                      <Box
                        component="span"
                        sx={{fontWeight: 500, color: kit.slate2}}>
                        (optional)
                      </Box>
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        color: kit.slate2,
                        marginTop: '2px',
                        marginBottom: '10px',
                      }}>
                      Share your experience and any comments about this{' '}
                      {revieweeType} with the Bidmii community
                    </Typography>
                    <BidmiiTextField
                      fullWidth
                      multiline
                      rows={4}
                      name="note"
                      id="outlined-multiline-static"
                      InputLabelProps={{shrink: true}}
                      placeholder={`My experience with this ${revieweeType}...`}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    padding: '20px 22px',
                    borderTop: `1px solid ${kit.mist}`,
                  }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: kit.slate,
                    }}>
                    Private feedback{' '}
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 500,
                        textTransform: 'none',
                        letterSpacing: 'normal',
                        color: kit.slate2,
                      }}>
                      (optional)
                    </Box>
                  </Typography>
                  <Typography
                    sx={{fontSize: 12.5, color: kit.slate2, marginTop: '2px'}}>
                    This feedback will never be shared with the {revieweeType}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: kit.ink,
                      marginTop: '16px',
                    }}>
                    How likely would you be to recommend this {revieweeType} to
                    a friend?
                  </Typography>
                  <FormControl
                    component="fieldset"
                    sx={{width: '100%', marginTop: '8px'}}>
                    <Box
                      sx={{display: 'flex', justifyContent: 'space-between'}}>
                      <Typography sx={{fontSize: 11.5, color: kit.slate2}}>
                        Not likely
                      </Typography>
                      <Typography sx={{fontSize: 11.5, color: kit.slate2}}>
                        Extremely likely
                      </Typography>
                    </Box>
                    <RadioGroup
                      row
                      aria-label="recommendationScore"
                      name="recommendationScore"
                      value={score}
                      onChange={(e) => setScore(e.target.value)}>
                      {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map(
                        (num) => (
                          <BidmiiRadio
                            key={num}
                            value={num}
                            label={num}
                            state={score}
                            labelPlacement="top"
                            style={{maxWidth: width > 600 ? 30 : 36}}
                          />
                        ),
                      )}
                    </RadioGroup>
                  </FormControl>
                  <Box sx={{marginTop: '12px'}}>
                    <BidmiiTextField
                      fullWidth
                      multiline
                      rows={4}
                      name="privateNote"
                      id="outlined-multiline-static"
                      InputLabelProps={{shrink: true}}
                      placeholder="Private comments"
                    />
                  </Box>
                </Box>
              </Card>
            </Box>
          </Box>
          <FixedFooter appbar={width <= 600} justifyContent="flex-end">
            <PrimaryButton type="submit" disabled={isSubmitting}>
              SUBMIT REVIEW
            </PrimaryButton>
          </FixedFooter>
        </form>
      </>
    );
  };

  const renderCompletionStep = () => {
    const isAlreadyComplete = review?.state === 'COMPLETE';

    return (
      <>
        <ToolbarHeader title="Review Complete" />
        <Box
          sx={{
            backgroundColor: kit.mist,
            padding: '24px',
            minHeight: width > 600 ? '70vh' : '100vh',
            display: 'flex',
            alignItems: 'center',
          }}>
          <Card
            radius="lg"
            sx={{padding: '32px 24px', textAlign: 'center', width: '100%'}}>
            <Typography
              sx={{
                fontFamily: fonts.serif,
                fontSize: 24,
                lineHeight: 1.25,
                letterSpacing: '-0.3px',
                color: kit.ink,
              }}>
              {isAlreadyComplete
                ? 'This project has been marked as complete. Great job!'
                : 'Thanks for using Bidmii!'}
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: 1.5,
                color: kit.slate,
                fontWeight: 500,
                marginTop: '14px',
              }}>
              Projects like yours help build transparency and security across
              the renovation market.
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: 1.5,
                color: kit.slate,
                fontWeight: 500,
                marginTop: '10px',
              }}>
              Our mission depends on you! Please, tell your friends about your
              Bidmii experience.
            </Typography>
            <Box sx={{display: 'flex', justifyContent: 'center'}}>
              <img
                src={'https://cdn.bidmii.com/media/web_assets/logo.webp'}
                alt="bidmii logo"
                style={{width: 100, marginTop: 24}}
              />
            </Box>
          </Card>
        </Box>

        {celebrationAnimation}
      </>
    );
  };

  const getDialogMaxWidth = () => {
    if (width <= 600) {
      return 'none';
    }
    // Approximately 1/3 of screen width, but with a reasonable max
    return Math.min(width * 0.33, 800);
  };

  // Kit-styled placeholder shaped like renderApprovalStep (the step this
  // flow lands on most often) -- avoids a layout jump once the RFP/proposal/
  // review fetch resolves, and keeps the loading state on the same visual
  // system as the rest of this dialog instead of the old raw MUI Skeleton +
  // theme.palette.gray styling.
  const renderLoadingSkeleton = () => {
    return (
      <>
        <ToolbarHeader title="" />
        <Box
          sx={{
            backgroundColor: kit.mist,
            padding: '20px 22px 32px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}>
          <Card radius="lg" sx={{padding: '12px'}}>
            <SkeletonBlock height={width > 600 ? 300 : 200} radius="md" />
          </Card>

          <Card sx={{padding: '20px 22px'}}>
            <SkeletonBlock
              width="70%"
              height={22}
              sx={{marginBottom: '10px'}}
            />
            <SkeletonBlock
              width="100%"
              height={16}
              sx={{marginBottom: '8px'}}
            />
            <SkeletonBlock
              width="90%"
              height={16}
              sx={{marginBottom: '20px'}}
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '20px',
              }}>
              <SkeletonBlock width={20} height={20} radius="sm" />
              <SkeletonBlock width={180} height={16} />
            </Box>

            <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
              <SkeletonBlock width={100} height={42} radius="pill" />
            </Box>
          </Card>
        </Box>
      </>
    );
  };

  // Real error card with a working retry -- replaces the old plain colored
  // banner (which had no way to recover and, worse, still rendered the
  // data-less approval step underneath it since `isLoading` goes false as
  // soon as `error` is set). `handleRetry` re-runs the exact same fetch.
  const renderErrorState = () => (
    <Box
      sx={{
        backgroundColor: kit.mist,
        minHeight: width > 600 ? '70vh' : '100vh',
        display: 'flex',
        alignItems: 'center',
        padding: '24px',
      }}>
      <Card
        radius="lg"
        sx={{padding: '40px 28px', textAlign: 'center', width: '100%'}}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: `${radius.pill}px`,
            backgroundColor: kit.dangerTint,
            color: kit.danger,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
          <RefreshIcon sx={{fontSize: 24}} />
        </Box>
        <Typography
          sx={{
            fontFamily: fonts.serif,
            fontSize: 22,
            lineHeight: 1.25,
            color: kit.ink,
          }}>
          We couldn't load this review
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: 1.5,
            color: kit.slate,
            fontWeight: 500,
            marginTop: '8px',
          }}>
          {error}
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={handleRetry}
          sx={{
            marginTop: '20px',
            height: 44,
            padding: '0 22px',
            borderRadius: `${radius.md}px`,
            border: 'none',
            backgroundColor: kit.blue,
            color: '#FFFFFF',
            fontFamily: 'inherit',
            fontSize: 14.5,
            fontWeight: 600,
            cursor: 'pointer',
          }}>
          Try again
        </Box>
      </Card>
    </Box>
  );

  const isLoading =
    (requestForProposal == null || proposal == null || review == null) &&
    !error;

  return (
    <Dialog
      open={true}
      onClose={() => {}}
      // @ts-expect-error ts-migrate(2322) FIXME: Type 'ForwardRefExoticComponent<RefAttributes<unkn... Remove this comment to see the full error message
      TransitionComponent={Transition}
      PaperProps={{
        style: {
          maxWidth: getDialogMaxWidth(),
          borderRadius: width <= 600 ? 0 : 10,
          overflowX: 'hidden',
        },
      }}
      fullScreen={width <= 600}>
      {error ? (
        renderErrorState()
      ) : (
        <>
          {isLoading && renderLoadingSkeleton()}
          {!isLoading && step === 'approval' && renderApprovalStep()}
          {!isLoading && step === 'review' && renderReviewStep()}
          {!isLoading && step === 'completion' && renderCompletionStep()}
        </>
      )}
    </Dialog>
  );
}
